import { openDB, DBSchema, IDBPDatabase } from 'idb';

interface SyncItem {
  id: string;
  type: 'case' | 'patient' | 'task' | 'inventory' | 'call_log' | 'work_log';
  action: 'create' | 'update' | 'delete';
  data: any;
  timestamp: number;
  retryCount: number;
  status: 'pending' | 'syncing' | 'failed' | 'synced';
  error?: string;
}

interface SyncDB extends DBSchema {
  'pending-syncs': {
    key: string;
    value: SyncItem;
    indexes: { 'by-status': string; 'by-timestamp': number };
  };
  'draft-forms': {
    key: string;
    value: {
      id: string;
      type: string;
      data: any;
      lastSaved: number;
    };
  };
}

class SyncManager {
  private db: IDBPDatabase<SyncDB> | null = null;
  private syncInProgress = false;
  private listeners: Set<(status: SyncStatus) => void> = new Set();

  async init() {
    if (this.db) return;

    this.db = await openDB<SyncDB>('clinic-flow-sync', 1, {
      upgrade(db) {
        // Store for pending sync operations
        const syncStore = db.createObjectStore('pending-syncs', { keyPath: 'id' });
        syncStore.createIndex('by-status', 'status');
        syncStore.createIndex('by-timestamp', 'timestamp');

        // Store for draft forms
        db.createObjectStore('draft-forms', { keyPath: 'id' });
      },
    });

    // Listen for online event to trigger sync
    window.addEventListener('online', () => this.syncAll());
  }

  // Save draft form
  async saveDraft(type: string, id: string, data: any) {
    if (!this.db) await this.init();
    
    await this.db!.put('draft-forms', {
      id: `${type}-${id}`,
      type,
      data,
      lastSaved: Date.now(),
    });

    this.notifyListeners({ type: 'draft-saved', message: 'Draft saved locally' });
  }

  // Get draft form
  async getDraft(type: string, id: string) {
    if (!this.db) await this.init();
    
    return await this.db!.get('draft-forms', `${type}-${id}`);
  }

  // Delete draft form
  async deleteDraft(type: string, id: string) {
    if (!this.db) await this.init();
    
    await this.db!.delete('draft-forms', `${type}-${id}`);
  }

  // Get all drafts
  async getAllDrafts() {
    if (!this.db) await this.init();
    
    return await this.db!.getAll('draft-forms');
  }

  // Queue an operation for sync
  async queueSync(
    type: SyncItem['type'],
    action: SyncItem['action'],
    data: any,
    itemId?: string
  ) {
    if (!this.db) await this.init();

    const id = itemId || `${type}-${action}-${Date.now()}`;
    const syncItem: SyncItem = {
      id,
      type,
      action,
      data,
      timestamp: Date.now(),
      retryCount: 0,
      status: 'pending',
    };

    await this.db!.put('pending-syncs', syncItem);
    this.notifyListeners({ 
      type: 'item-queued', 
      message: 'Changes will sync when online',
      count: await this.getPendingCount()
    });

    // Try to sync immediately if online
    if (navigator.onLine) {
      this.syncAll();
    }

    return id;
  }

  // Get pending sync count
  async getPendingCount(): Promise<number> {
    if (!this.db) await this.init();
    
    const items = await this.db!.getAllFromIndex('pending-syncs', 'by-status', 'pending');
    return items.length;
  }

  // Get all pending syncs
  async getPendingSyncs(): Promise<SyncItem[]> {
    if (!this.db) await this.init();
    
    return await this.db!.getAll('pending-syncs');
  }

  // Sync all pending operations
  async syncAll() {
    if (this.syncInProgress || !navigator.onLine) return;

    if (!this.db) await this.init();

    this.syncInProgress = true;
    this.notifyListeners({ type: 'sync-started', message: 'Syncing data...' });

    try {
      const pendingItems = await this.db!.getAllFromIndex(
        'pending-syncs',
        'by-status',
        'pending'
      );

      if (pendingItems.length === 0) {
        this.syncInProgress = false;
        return;
      }

      let successCount = 0;
      let failCount = 0;

      for (const item of pendingItems) {
        try {
          // Update status to syncing
          await this.db!.put('pending-syncs', { ...item, status: 'syncing' });

          // Perform the sync operation
          await this.performSync(item);

          // Mark as synced
          await this.db!.delete('pending-syncs', item.id);
          successCount++;

          // Delete associated draft if exists
          await this.deleteDraft(item.type, item.id);
        } catch (error) {
          failCount++;
          const retryCount = item.retryCount + 1;

          if (retryCount >= 3) {
            // Mark as failed after 3 retries
            await this.db!.put('pending-syncs', {
              ...item,
              status: 'failed',
              error: (error as Error).message,
              retryCount,
            });
          } else {
            // Reset to pending for retry
            await this.db!.put('pending-syncs', {
              ...item,
              status: 'pending',
              retryCount,
            });
          }
        }
      }

      const remaining = await this.getPendingCount();
      
      if (failCount === 0) {
        this.notifyListeners({
          type: 'sync-completed',
          message: `Successfully synced ${successCount} items`,
          count: remaining,
        });
      } else {
        this.notifyListeners({
          type: 'sync-partial',
          message: `Synced ${successCount} items, ${failCount} failed`,
          count: remaining,
        });
      }
    } catch (error) {
      this.notifyListeners({
        type: 'sync-error',
        message: 'Sync failed: ' + (error as Error).message,
      });
    } finally {
      this.syncInProgress = false;
    }
  }

  // Perform actual sync operation
  private async performSync(item: SyncItem) {
    // Legacy entries do not carry an authenticated owner, expected revision or
    // command key. Keep them for review; direct replay could lose local work or
    // apply it to the wrong session. New Lab Work saves use the command flow.
    if (item.type === 'case') {
      throw new Error('This saved Lab Work entry needs review in Lab Work before it can be submitted.');
    }
    const { supabase } = await import('@/integrations/supabase/client');

    switch (item.type) {
      case 'patient':
        if (item.action === 'create') {
          await supabase.from('patients').insert(item.data);
        } else if (item.action === 'update') {
          await supabase.from('patients').update(item.data).eq('id', item.data.id);
        }
        break;

      case 'task':
        if (item.action === 'create') {
          await supabase.from('tasks').insert(item.data);
        } else if (item.action === 'update') {
          await supabase.from('tasks').update(item.data).eq('id', item.data.id);
        }
        break;

      case 'inventory':
        if (item.action === 'create') {
          await supabase.from('inventory_items').insert(item.data);
        } else if (item.action === 'update') {
          await supabase.from('inventory_items').update(item.data).eq('id', item.data.id);
        }
        break;

      case 'call_log':
        if (item.action === 'create') {
          await supabase.from('call_logs').insert(item.data);
        } else if (item.action === 'update') {
          await supabase.from('call_logs').update(item.data).eq('id', item.data.id);
        }
        break;

      case 'work_log':
        if (item.action === 'create') {
          await supabase.from('assistant_work_logs').insert(item.data);
        } else if (item.action === 'update') {
          await supabase.from('assistant_work_logs').update(item.data).eq('id', item.data.id);
        }
        break;

      default:
        throw new Error(`Unknown sync type: ${item.type}`);
    }
  }

  // Retry failed syncs
  async retryFailedSyncs() {
    if (!this.db) await this.init();

    const failedItems = await this.db!.getAllFromIndex('pending-syncs', 'by-status', 'failed');

    for (const item of failedItems) {
      await this.db!.put('pending-syncs', { ...item, status: 'pending', retryCount: 0 });
    }

    this.syncAll();
  }

  // Clear all sync data (use with caution)
  async clearAll() {
    if (!this.db) await this.init();

    await this.db!.clear('pending-syncs');
    await this.db!.clear('draft-forms');
    
    this.notifyListeners({ type: 'cleared', message: 'All sync data cleared' });
  }

  // Subscribe to sync status updates
  subscribe(callback: (status: SyncStatus) => void) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  private notifyListeners(status: SyncStatus) {
    this.listeners.forEach(listener => listener(status));
  }
}

export interface SyncStatus {
  type: 'draft-saved' | 'item-queued' | 'sync-started' | 'sync-completed' | 'sync-partial' | 'sync-error' | 'cleared';
  message: string;
  count?: number;
}

export const syncManager = new SyncManager();
